import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/i18n/I18nProvider";
import { TIDS } from "@/constants/testIds";
import { createCheckout, createLead } from "@/lib/api";
import { toast } from "sonner";
import { Lock, ShieldCheck, EyeOff, CreditCard, Loader2, Check, X } from "lucide-react";

const CheckoutDialog = ({ open, onOpenChange, pkg }) => {
  const { t } = useI18n();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [vat, setVat] = useState("");
  const [method, setMethod] = useState("nexi");
  const [submitting, setSubmitting] = useState(false);

  if (!pkg) return null;
  const isContactOnly = pkg.contactOnly;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isContactOnly) {
        await createLead({
          name: fullName, email, company: company || null,
          message: `Richiesta pacchetto ${pkg.code} · ${pkg.name}`, source: "premium-contact",
        });
        toast.success(t.lang === "en" ? "Request sent — we'll contact you shortly." : "Richiesta inviata — ti contatteremo a breve.");
        onOpenChange(false);
        return;
      }

      const res = await createCheckout({
        package_id: pkg.id,
        package_code: pkg.code,
        package_name: pkg.name,
        amount: pkg.price * 100,
        currency: "EUR",
        customer: { full_name: fullName, email, company: company || null, vat_number: vat || null },
        payment_method: method,
      });

      if (res.configured && res.redirect_url) {
        toast.success(t.lang === "en" ? "Redirecting to secure payment..." : "Reindirizzamento al pagamento sicuro...");
        window.location.href = res.redirect_url;
      } else {
        // Fallback when gateway is not configured — store as lead
        await createLead({
          name: fullName, email, company: company || null,
          message: `Acquisto richiesto: ${pkg.code} · ${pkg.name} (${method.toUpperCase()}) — €${pkg.price}`,
          source: "checkout-pending",
        });
        toast.success(t.checkout.configureWarning);
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante il checkout.");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={TIDS.checkoutDialog} className="max-w-3xl rounded-2xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
        <VisuallyHidden.Root>
          <DialogTitle>{t.checkout.title}</DialogTitle>
          <DialogDescription>{t.checkout.summary}: {pkg.name} — €{pkg.price}</DialogDescription>
        </VisuallyHidden.Root>
        <div className="grid md:grid-cols-5">
          {/* Form */}
          <div className="md:col-span-3 p-6 md:p-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Zuin Digital Consulting</div>
                <h2 className="font-display text-xl md:text-2xl font-semibold text-[#111111]">{t.checkout.title}</h2>
              </div>
              <button onClick={() => onOpenChange(false)} className="w-9 h-9 rounded-full border border-[#E5E7EB] flex items-center justify-center hover:bg-[#F5F7FA]" data-testid={TIDS.checkoutCancel}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-[#6B7280] mb-3">
                  {t.checkout.customerData}
                </div>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{t.checkout.fullName} *</Label>
                    <Input data-testid={TIDS.checkoutFullName} value={fullName} onChange={(e) => setFullName(e.target.value)} required className="rounded-xl h-11 border-[#E5E7EB]" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">{t.checkout.email} *</Label>
                    <Input data-testid={TIDS.checkoutEmail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-xl h-11 border-[#E5E7EB]" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{t.checkout.company}</Label>
                      <Input data-testid={TIDS.checkoutCompany} value={company} onChange={(e) => setCompany(e.target.value)} className="rounded-xl h-11 border-[#E5E7EB]" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium">{t.checkout.vat}</Label>
                      <Input data-testid={TIDS.checkoutVat} value={vat} onChange={(e) => setVat(e.target.value)} className="rounded-xl h-11 border-[#E5E7EB]" />
                    </div>
                  </div>
                </div>
              </div>

              {!isContactOnly && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-[#6B7280] mb-3">
                    {t.checkout.paymentMethod}
                  </div>
                  <div className="space-y-2.5">
                    <PaymentOption
                      testid={TIDS.checkoutMethodNexi}
                      selected={method === "nexi"}
                      onClick={() => setMethod("nexi")}
                      title={t.checkout.nexi}
                      sub={t.checkout.nexiSub}
                      logo="nexi"
                    />
                    <PaymentOption
                      testid={TIDS.checkoutMethodViva}
                      selected={method === "viva"}
                      onClick={() => setMethod("viva")}
                      title={t.checkout.viva}
                      sub={t.checkout.vivaSub}
                      logo="viva"
                    />
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid={TIDS.checkoutSubmit}
                className="btn-blue w-full justify-center disabled:opacity-60 h-12 text-base"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />{t.checkout.processing}</>
                ) : (
                  <><Lock className="w-4 h-4" />{isContactOnly ? t.pricing.contactUs : t.checkout.submit}</>
                )}
              </button>

              <div className="flex items-center justify-center gap-4 pt-2 text-[11px] text-[#6B7280]">
                <div className="flex items-center gap-1.5"><Lock className="w-3 h-3" />{t.checkout.trustSsl}</div>
                <div className="flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" />{t.checkout.trustPayments}</div>
                <div className="flex items-center gap-1.5"><EyeOff className="w-3 h-3" />{t.checkout.trustPrivacy}</div>
              </div>
            </form>
          </div>

          {/* Order Summary */}
          <div className="md:col-span-2 bg-[#F5F7FA] p-6 md:p-8 border-l border-[#E5E7EB]">
            <div className="text-xs font-medium uppercase tracking-wider text-[#6B7280] mb-4">
              {t.checkout.summary}
            </div>

            <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[#6B7280] mb-1">{pkg.code}</div>
                  <div className="font-display text-base font-semibold text-[#111111]">{pkg.name}</div>
                  <div className="text-xs text-[#6B7280] mt-0.5">{pkg.tagline}</div>
                </div>
                <div className="font-display text-lg font-semibold text-[#111111] whitespace-nowrap">€{pkg.price.toLocaleString("it-IT")}</div>
              </div>

              <ul className="mt-4 space-y-1.5">
                {pkg.features.slice(0, 4).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-[#111111]">
                    <Check className="w-3 h-3 text-[#2563EB] flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-5 pt-5 border-t border-[#E5E7EB] flex items-baseline justify-between">
              <span className="text-sm font-medium text-[#111111]">{t.checkout.total}</span>
              <span className="font-display text-2xl font-semibold text-[#111111]">€{pkg.price.toLocaleString("it-IT")}</span>
            </div>

            <div className="mt-6 space-y-2">
              <Badge label="SSL · 256 bit" />
              <Badge label="3D Secure · Nexi / Viva" />
              <Badge label="GDPR Compliant" />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Badge = ({ label }) => (
  <div className="flex items-center gap-2 text-xs text-[#6B7280]">
    <div className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
    {label}
  </div>
);

const PaymentOption = ({ selected, onClick, title, sub, testid, logo }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all text-left ${
      selected ? "border-[#2563EB] bg-[#2563EB]/5 shadow-[0_0_0_3px_rgba(37,99,235,0.08)]" : "border-[#E5E7EB] hover:border-[#111111]/30"
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected ? "border-[#2563EB]" : "border-[#D1D5DB]"}`}>
        {selected && <div className="w-2 h-2 rounded-full bg-[#2563EB]" />}
      </div>
      <div>
        <div className="text-sm font-medium text-[#111111]">{title}</div>
        <div className="text-xs text-[#6B7280]">{sub}</div>
      </div>
    </div>
    <div className="flex items-center gap-1">
      {logo === "nexi" ? (
        <div className="px-2 py-1 rounded-md bg-[#111111] text-white text-[10px] font-bold tracking-tight">NEXI</div>
      ) : (
        <div className="px-2 py-1 rounded-md bg-[#1A4FE2] text-white text-[10px] font-bold tracking-tight">VIVA</div>
      )}
      <CreditCard className="w-4 h-4 text-[#6B7280]" />
    </div>
  </button>
);

export default CheckoutDialog;
